<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEvents } from '@/composables/useEvents'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import OverflowMenu from '@/components/shared/OverflowMenu.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import HeaderCreateButton from '@/components/shared/HeaderCreateButton.vue'
import { getEventVouchers, createVoucher, updateVoucher, deleteVoucher } from '@/api/vouchers'
import { listEditions, listRaces } from '@/api/events'
import type { Voucher, CreateVoucherPayload, UpdateVoucherPayload } from '@/types/voucher'
import type { Edition, Race } from '@/types/events'

const { t } = useI18n()
const route = useRoute()
const eventSlug = route.params['eventSlug'] as string

const { event, fetchEvent } = useEvents()

const vouchers = ref<Voucher[]>([])
const editions = ref<Edition[]>([])
const races = ref<Race[]>([])
const loading = ref(true)
const actionLoading = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

// Modal state
const showModal = ref(false)
const editingVoucher = ref<Voucher | null>(null)
const isEditMode = computed(() => editingVoucher.value !== null)

// Form state
const form = ref({
  code: '',
  type: 'percentage' as 'percentage' | 'fixed_amount',
  value: 0,
  applies_to: 'registration_fee' as 'registration_fee' | 'registration_and_options',
  scope: 'event' as 'event' | 'edition' | 'race',
  edition_id: null as number | null,
  race_id: null as number | null,
  max_uses: null as number | null,
  valid_from: '',
  valid_until: '',
  active: true,
})

const formErrors = ref<Record<string, string[]>>({})

// Delete confirmation
const confirmDeleteId = ref<number | null>(null)
const confirmDeleteCode = ref('')

// Computed: races filtered by selected edition
const filteredRaces = computed(() => {
  if (!form.value.edition_id) return []
  return races.value.filter(r => r.edition_id === form.value.edition_id)
})

// Watch scope changes to reset dependent fields
watch(() => form.value.scope, (newScope) => {
  if (newScope === 'event') {
    form.value.edition_id = null
    form.value.race_id = null
  } else if (newScope === 'edition') {
    form.value.race_id = null
  }
})

// Watch edition changes to reset race
watch(() => form.value.edition_id, () => {
  form.value.race_id = null
})

onMounted(async () => {
  await Promise.all([loadData(), fetchEvent(eventSlug)])
})

async function loadData(): Promise<void> {
  loading.value = true
  try {
    const [voucherData, editionData] = await Promise.all([
      getEventVouchers(eventSlug),
      listEditions(eventSlug),
    ])
    vouchers.value = voucherData
    editions.value = editionData

    // Load races for all editions
    const racePromises = editionData.map(ed => listRaces(eventSlug, ed.slug))
    const raceResults = await Promise.all(racePromises)
    races.value = raceResults.flat()
  } finally {
    loading.value = false
  }
}

function openCreateModal(): void {
  editingVoucher.value = null
  form.value = {
    code: '',
    type: 'percentage',
    value: 0,
    applies_to: 'registration_fee',
    scope: 'event',
    edition_id: null,
    race_id: null,
    max_uses: null,
    valid_from: '',
    valid_until: '',
    active: true,
  }
  formErrors.value = {}
  successMessage.value = ''
  errorMessage.value = ''
  showModal.value = true
}

function openEditModal(voucher: Voucher): void {
  editingVoucher.value = voucher
  form.value = {
    code: voucher.code,
    type: voucher.type,
    value: parseFloat(voucher.value),
    applies_to: voucher.applies_to,
    scope: voucher.scope,
    edition_id: voucher.edition_id ?? null,
    race_id: voucher.race_id ?? null,
    max_uses: voucher.max_uses,
    valid_from: voucher.valid_from ? voucher.valid_from.slice(0, 16) : '',
    valid_until: voucher.valid_until ? voucher.valid_until.slice(0, 16) : '',
    active: voucher.active,
  }
  formErrors.value = {}
  successMessage.value = ''
  errorMessage.value = ''
  showModal.value = true
}

function closeModal(): void {
  if (actionLoading.value) return
  showModal.value = false
  editingVoucher.value = null
}

function handleKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && !actionLoading.value) {
    closeModal()
  }
}

watch(showModal, (open) => {
  if (open) {
    document.addEventListener('keydown', handleKeyDown)
  } else {
    document.removeEventListener('keydown', handleKeyDown)
  }
})

function generateCode(): void {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  const groups: string[] = []
  for (let g = 0; g < 4; g++) {
    let group = ''
    for (let c = 0; c < 4; c++) {
      group += chars[Math.floor(Math.random() * chars.length)]
    }
    groups.push(group)
  }
  form.value.code = groups.join('-')
}

async function handleSubmit(): Promise<void> {
  actionLoading.value = true
  formErrors.value = {}
  errorMessage.value = ''

  try {
    if (isEditMode.value && editingVoucher.value) {
      const payload: UpdateVoucherPayload = {
        type: form.value.type,
        value: form.value.value,
        applies_to: form.value.applies_to,
        scope: form.value.scope,
        edition_id: form.value.edition_id,
        race_id: form.value.race_id,
        max_uses: form.value.max_uses,
        valid_from: form.value.valid_from || null,
        valid_until: form.value.valid_until || null,
        active: form.value.active,
      }
      await updateVoucher(eventSlug, editingVoucher.value.id, payload)
      successMessage.value = t('vouchers.updated_successfully')
    } else {
      const payload: CreateVoucherPayload = {
        code: form.value.code,
        type: form.value.type,
        value: form.value.value,
        applies_to: form.value.applies_to,
        scope: form.value.scope,
        edition_id: form.value.edition_id,
        race_id: form.value.race_id,
        max_uses: form.value.max_uses,
        valid_from: form.value.valid_from || null,
        valid_until: form.value.valid_until || null,
        active: form.value.active,
      }
      await createVoucher(eventSlug, payload)
      successMessage.value = t('vouchers.created_successfully')
    }
    closeModal()
    await loadData()
  } catch (err: unknown) {
    const error = err as { response?: { status: number; data?: { errors?: Record<string, string[]>; message?: string } } }
    if (error.response?.status === 422 && error.response.data?.errors) {
      formErrors.value = error.response.data.errors
    } else {
      errorMessage.value = error.response?.data?.message ?? t('common.error')
    }
  } finally {
    actionLoading.value = false
  }
}

function requestDelete(voucher: Voucher): void {
  confirmDeleteId.value = voucher.id
  confirmDeleteCode.value = voucher.code
  successMessage.value = ''
  errorMessage.value = ''
}

function cancelDelete(): void {
  confirmDeleteId.value = null
  confirmDeleteCode.value = ''
}

async function confirmDelete(): Promise<void> {
  if (confirmDeleteId.value === null) return
  actionLoading.value = true
  errorMessage.value = ''
  try {
    await deleteVoucher(eventSlug, confirmDeleteId.value)
    successMessage.value = t('vouchers.deleted_successfully')
    confirmDeleteId.value = null
    confirmDeleteCode.value = ''
    await loadData()
  } catch (err: unknown) {
    const error = err as { response?: { data?: { message?: string } } }
    errorMessage.value = error.response?.data?.message ?? t('common.error')
    confirmDeleteId.value = null
  } finally {
    actionLoading.value = false
  }
}

async function toggleActive(voucher: Voucher): Promise<void> {
  actionLoading.value = true
  errorMessage.value = ''
  try {
    await updateVoucher(eventSlug, voucher.id, {
      type: voucher.type,
      value: parseFloat(voucher.value),
      applies_to: voucher.applies_to,
      scope: voucher.scope,
      edition_id: voucher.edition_id ?? null,
      race_id: voucher.race_id ?? null,
      max_uses: voucher.max_uses,
      valid_from: voucher.valid_from,
      valid_until: voucher.valid_until,
      active: !voucher.active,
    })
    await loadData()
  } catch {
    errorMessage.value = t('common.error')
  } finally {
    actionLoading.value = false
  }
}

function formatScope(voucher: Voucher): string {
  if (voucher.scope === 'event') return t('vouchers.scope_event')
  if (voucher.scope === 'edition') return t('vouchers.scope_edition')
  return t('vouchers.scope_race')
}

function formatType(type: string): string {
  if (type === 'percentage') return t('vouchers.type_percentage')
  return t('vouchers.type_fixed_amount')
}

function formatValue(voucher: Voucher): string {
  if (voucher.type === 'percentage') return `${voucher.value}%`
  return `EUR ${voucher.value}`
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString()
}
</script>

<template>
  <OrganizerLayout>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Breadcrumb -->
      <nav aria-label="breadcrumb" class="flex items-center gap-2 text-xs text-slate-500 mb-6 flex-wrap">
        <RouterLink to="/organizer/dashboard" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.dashboard') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink to="/organizer/events" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.events') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}`" class="hover:text-slate-300 transition-colors">
          <template v-if="event">{{ event.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <span class="text-slate-300">{{ t('vouchers.title') }}</span>
      </nav>

      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-3xl font-bold text-white">
            <template v-if="event">{{ event.name }}</template>
            <span v-else class="text-slate-500">{{ t('vouchers.title') }}</span>
          </h1>
          <p class="text-slate-400 mt-1">{{ t('vouchers.title') }}</p>
        </div>
        <HeaderCreateButton :label="t('vouchers.create')" compact @click="openCreateModal" />
      </div>

      <!-- Success message -->
      <div
        v-if="successMessage"
        class="mb-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
      >
        {{ successMessage }}
      </div>

      <!-- Error message -->
      <div
        v-if="errorMessage"
        class="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      >
        {{ errorMessage }}
      </div>

      <!-- Loading state -->
      <AppSpinner v-if="loading" />

      <!-- Empty state -->
      <div
        v-else-if="vouchers.length === 0"
        class="rounded-xl border border-dashed border-slate-700 px-8 py-12 text-center"
      >
        <p class="text-slate-400 text-sm">{{ t('vouchers.no_vouchers') }}</p>
      </div>

      <!-- Vouchers table -->
      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead>
            <tr class="border-b border-slate-700">
              <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">{{ t('vouchers.code') }}</th>
              <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">{{ t('vouchers.type') }}</th>
              <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">{{ t('vouchers.value') }}</th>
              <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">{{ t('vouchers.scope') }}</th>
              <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">{{ t('vouchers.used_count') }}</th>
              <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">{{ t('vouchers.valid_from') }}</th>
              <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">{{ t('vouchers.valid_until') }}</th>
              <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">{{ t('vouchers.active') }}</th>
              <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider text-right"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="v in vouchers" :key="v.id" class="border-b border-slate-700/50 hover:bg-slate-800/50 transition-colors">
              <td class="px-3 py-3 text-sm font-mono text-white">{{ v.code }}</td>
              <td class="px-3 py-3 text-sm text-slate-300">{{ formatType(v.type) }}</td>
              <td class="px-3 py-3 text-sm text-slate-300">{{ formatValue(v) }}</td>
              <td class="px-3 py-3 text-sm text-slate-300">{{ formatScope(v) }}</td>
              <td class="px-3 py-3">
                <span class="text-sm text-slate-300">
                  {{ v.used_count }}<span v-if="v.max_uses !== null"> / {{ v.max_uses }}</span><span v-else> / ∞</span>
                </span>
                <div v-if="v.max_uses !== null" class="mt-1 h-1 w-24 rounded-full bg-slate-700 overflow-hidden">
                  <div
                    class="h-full rounded-full transition-all"
                    :class="v.used_count / v.max_uses >= 0.9 ? 'bg-red-400' : v.used_count / v.max_uses >= 0.75 ? 'bg-amber-400' : 'bg-emerald-400'"
                    :style="{ width: `${Math.min(100, Math.round((v.used_count / v.max_uses) * 100))}%` }"
                  />
                </div>
              </td>
              <td class="px-3 py-3 text-sm text-slate-300">{{ formatDate(v.valid_from) }}</td>
              <td class="px-3 py-3 text-sm text-slate-300">{{ formatDate(v.valid_until) }}</td>
              <td class="px-3 py-3">
                <span
                  :class="v.active
                    ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                    : 'bg-slate-700 border-slate-600 text-slate-400'"
                  class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium"
                >
                  {{ v.active ? t('vouchers.active') : t('vouchers.inactive') }}
                </span>
              </td>
              <td class="px-3 py-3 text-right">
                <OverflowMenu teleport menu-class="w-40">
                  <button
                    type="button"
                    class="w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors"
                    @click="toggleActive(v)"
                  >
                    {{ v.active ? t('vouchers.deactivate') : t('vouchers.activate') }}
                  </button>
                  <button
                    type="button"
                    class="w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors"
                    @click="openEditModal(v)"
                  >
                    {{ t('vouchers.edit') }}
                  </button>
                  <hr class="border-slate-700 my-1" />
                  <button
                    type="button"
                    :disabled="v.used_count > 0"
                    :title="v.used_count > 0 ? t('vouchers.cannot_delete_used') : ''"
                    class="w-full text-left px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-slate-700/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    @click="requestDelete(v)"
                  >
                    {{ t('vouchers.delete') }}
                  </button>
                </OverflowMenu>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </main>

    <!-- Create/Edit slide-over -->
    <Teleport to="body">
      <div
        v-if="showModal"
        class="fixed inset-0 z-50 flex justify-end"
      >
        <!-- Dimmed backdrop -->
        <div
          class="absolute inset-0 bg-black/60 backdrop-blur-sm"
          aria-hidden="true"
          @click="closeModal"
        />

        <!-- Slide-over panel -->
        <div
          class="relative z-10 flex flex-col bg-slate-800 border-l border-slate-700 w-full max-w-xl h-full shadow-2xl"
          role="dialog"
          aria-modal="true"
          :aria-label="isEditMode ? t('vouchers.edit') : t('vouchers.create')"
        >
          <!-- Header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-700 shrink-0">
            <h2 class="text-lg font-bold text-white">
              {{ isEditMode ? t('vouchers.edit') : t('vouchers.create') }}
            </h2>
            <button
              type="button"
              class="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
              :aria-label="t('common.close')"
              @click="closeModal"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form @submit.prevent="handleSubmit" class="flex flex-col flex-1 min-h-0">
            <!-- Scrollable body -->
            <div class="flex-1 overflow-y-auto px-6 py-6 space-y-4">
              <!-- Code field (create only) -->
          <div v-if="!isEditMode">
            <label class="block text-sm font-medium text-slate-300 mb-1">{{ t('vouchers.code') }}</label>
            <div class="flex gap-2">
              <input
                v-model="form.code"
                type="text"
                class="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                required
              />
              <button
                type="button"
                class="shrink-0 text-sm font-medium text-emerald-400 hover:text-emerald-300 border border-emerald-500/30 hover:border-emerald-500/60 rounded-lg px-3 py-2 transition-colors"
                @click="generateCode"
              >
                {{ t('vouchers.generate_code') }}
              </button>
            </div>
            <p v-if="formErrors.code" class="mt-1 text-xs text-red-400">{{ formErrors.code[0] }}</p>
          </div>

          <!-- Code display (edit mode, read-only) -->
          <div v-else>
            <label class="block text-sm font-medium text-slate-300 mb-1">{{ t('vouchers.code') }}</label>
            <div class="bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-400 font-mono">
              {{ form.code }}
            </div>
          </div>

          <!-- Type -->
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">{{ t('vouchers.type') }}</label>
            <select
              v-model="form.type"
              class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            >
              <option value="percentage">{{ t('vouchers.type_percentage') }}</option>
              <option value="fixed_amount">{{ t('vouchers.type_fixed_amount') }}</option>
            </select>
            <p v-if="formErrors.type" class="mt-1 text-xs text-red-400">{{ formErrors.type[0] }}</p>
          </div>

          <!-- Value -->
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">
              {{ t('vouchers.value') }}
              <span v-if="form.type === 'percentage'" class="text-slate-500">(%)</span>
              <span v-else class="text-slate-500">(EUR)</span>
            </label>
            <input
              v-model.number="form.value"
              type="number"
              step="0.01"
              min="0.01"
              :max="form.type === 'percentage' ? 100 : undefined"
              class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              required
            />
            <p v-if="formErrors.value" class="mt-1 text-xs text-red-400">{{ formErrors.value[0] }}</p>
          </div>

          <!-- Applies to -->
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">{{ t('vouchers.applies_to') }}</label>
            <select
              v-model="form.applies_to"
              class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            >
              <option value="registration_fee">{{ t('vouchers.applies_to_registration_fee') }}</option>
              <option value="registration_and_options">{{ t('vouchers.applies_to_registration_and_options') }}</option>
            </select>
            <p v-if="formErrors.applies_to" class="mt-1 text-xs text-red-400">{{ formErrors.applies_to[0] }}</p>
          </div>

          <!-- Scope -->
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">{{ t('vouchers.scope') }}</label>
            <select
              v-model="form.scope"
              class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            >
              <option value="event">{{ t('vouchers.scope_event') }}</option>
              <option value="edition">{{ t('vouchers.scope_edition') }}</option>
              <option value="race">{{ t('vouchers.scope_race') }}</option>
            </select>
            <p v-if="formErrors.scope" class="mt-1 text-xs text-red-400">{{ formErrors.scope[0] }}</p>
          </div>

          <!-- Edition dropdown (when scope is edition or race) -->
          <div v-if="form.scope === 'edition' || form.scope === 'race'">
            <label class="block text-sm font-medium text-slate-300 mb-1">Edition</label>
            <select
              v-model.number="form.edition_id"
              class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              required
            >
              <option :value="null" disabled>{{ t('vouchers.edition_placeholder') }}</option>
              <option v-for="ed in editions" :key="ed.id" :value="ed.id">{{ ed.name }}</option>
            </select>
            <p v-if="formErrors.edition_id" class="mt-1 text-xs text-red-400">{{ formErrors.edition_id[0] }}</p>
          </div>

          <!-- Race dropdown (when scope is race) -->
          <div v-if="form.scope === 'race'">
            <label class="block text-sm font-medium text-slate-300 mb-1">Race</label>
            <select
              v-model.number="form.race_id"
              class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              required
              :disabled="!form.edition_id"
            >
              <option :value="null" disabled>{{ t('vouchers.race_placeholder') }}</option>
              <option v-for="r in filteredRaces" :key="r.id" :value="r.id">{{ r.name }}</option>
            </select>
            <p v-if="formErrors.race_id" class="mt-1 text-xs text-red-400">{{ formErrors.race_id[0] }}</p>
          </div>

          <!-- Max uses -->
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">
              {{ t('vouchers.max_uses') }}
              <span class="text-slate-500">({{ t('vouchers.max_uses_unlimited') }})</span>
            </label>
            <input
              v-model.number="form.max_uses"
              type="number"
              min="1"
              :placeholder="t('vouchers.max_uses_unlimited')"
              class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
            <p v-if="formErrors.max_uses" class="mt-1 text-xs text-red-400">{{ formErrors.max_uses[0] }}</p>
          </div>

          <!-- Valid from -->
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">{{ t('vouchers.valid_from') }}</label>
            <input
              v-model="form.valid_from"
              type="datetime-local"
              class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
            <p v-if="formErrors.valid_from" class="mt-1 text-xs text-red-400">{{ formErrors.valid_from[0] }}</p>
          </div>

          <!-- Valid until -->
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">{{ t('vouchers.valid_until') }}</label>
            <input
              v-model="form.valid_until"
              type="datetime-local"
              class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
            <p v-if="formErrors.valid_until" class="mt-1 text-xs text-red-400">{{ formErrors.valid_until[0] }}</p>
          </div>

              <!-- Active toggle -->
              <div class="flex items-center gap-2">
                <input
                  v-model="form.active"
                  type="checkbox"
                  id="voucher-active"
                  class="rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500"
                />
                <label for="voucher-active" class="text-sm text-slate-300">{{ t('vouchers.active') }}</label>
              </div>
            </div>

            <!-- Sticky footer -->
            <div class="shrink-0 flex items-center gap-3 justify-end px-6 py-4 border-t border-slate-700">
              <button
                type="button"
                :disabled="actionLoading"
                class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
                @click="closeModal"
              >
                {{ t('common.cancel') }}
              </button>
              <button
                type="submit"
                :disabled="actionLoading"
                class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-2"
              >
                <svg v-if="actionLoading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {{ actionLoading ? t('common.loading') : t('common.save') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <ConfirmDialog
      :open="confirmDeleteId !== null"
      :title="t('vouchers.delete')"
      :body="`${t('vouchers.delete_confirm')} (${confirmDeleteCode})`"
      :confirm-label="t('vouchers.delete')"
      :loading="actionLoading"
      @update:open="cancelDelete"
      @confirm="confirmDelete"
    />
  </OrganizerLayout>
</template>
